import {
  liftTarget,
  canJoin,
  joinPoint,
  canSplit,
  ReplaceAroundStep,
  findWrapping,
} from "prosemirror-transform";
import { Fragment, Slice } from "prosemirror-model";
import {
  NodeSelection,
  Selection,
  TextSelection,
  AllSelection,
} from "prosemirror-state";
import { cursorParagraphBefore } from "components/ProseMirror/Utils";
import { blockElementLastChild } from "components/ProseMirror/commands/commandsBlock";

// :: (EditorState, ?(tr: Transaction)) → bool
// Delete the selection, if there is one.
function deleteSelection(state, dispatch) {
  if (state.selection.empty) {
    return false;
  }
  if (dispatch) {
    dispatch(state.tr.deleteSelection().scrollIntoView());
  }
  return true;
}

// :: (EditorState, ?(tr: Transaction), ?EditorView) → bool
// If the selection is empty and at the start of a textblock, try to
// reduce the distance between that block and the one before it—if
// there's a block directly before it that can be joined, join them.
// If not, try to move the selected block closer to the next one in
// the document structure by lifting it out of its parent or moving it
// into a parent of the previous block. Will use the view for accurate
// (bidi-aware) start-of-textblock detection if given.
function joinBackward(state, dispatch, view) {
  // console.log('joinBackward')

  // Variables
  var ref = state.selection;
  var $cursor = ref.$cursor;

  // Can't process if:
  if (
    // No cursor
    !$cursor ||
    (view
      ? // Find out whether the selection is at the end of a textblock
        // when moving in a given direction
        // https://prosemirror.net/docs/ref/#view.EditorView.endOfTextblock
        !view.endOfTextblock("backward", state)
      : // What this check does?
        $cursor.parentOffset > 0)
  ) {
    return false;
  }

  var $cut = findCutBefore($cursor);

  // If there is no node before this, try to lift
  if (!$cut) {
    console.log("joinBackward.nocut");
    var range = $cursor.blockRange(),
      target = range && liftTarget(range);
    if (target == null) {
      return false;
    }
    if (dispatch) {
      dispatch(state.tr.lift(range, target).scrollIntoView());
    }
    return true;
  }

  var before = $cut.nodeBefore;

  // Apply the joining algorithm
  if (!before.type.spec.isolating && deleteBarrier(state, $cut, dispatch)) {
    console.log("joinBackward.deleteBarrier");
    return true;
  }

  // If the node below has no content and the node above is
  // selectable, delete the node below and select the one above.
  if (
    $cursor.parent.content.size == 0 &&
    (textblockAt(before, "end") || NodeSelection.isSelectable(before))
  ) {
    console.log("joinBackward.in2");

    if (dispatch) {
      var tr = state.tr.deleteRange($cursor.before(), $cursor.after());
      tr.setSelection(
        textblockAt(before, "end")
          ? Selection.findFrom(tr.doc.resolve(tr.mapping.map($cut.pos, -1)), -1)
          : NodeSelection.create(tr.doc, $cut.pos - before.nodeSize)
      );
      dispatch(tr.scrollIntoView());
    }
    return true;
  }

  // If the node before is an atom, delete it
  if (before.isAtom && $cut.depth == $cursor.depth - 1) {
    console.log("joinBackward.atom");

    if (dispatch) {
      dispatch(
        state.tr.delete($cut.pos - before.nodeSize, $cut.pos).scrollIntoView()
      );
    }
    return true;
  }

  return false;
}

function textblockAt(node, side) {
  for (; node; node = side == "start" ? node.firstChild : node.lastChild) {
    if (node.isTextblock) {
      return true;
    }
  }
  return false;
}

// :: (EditorState, ?(tr: Transaction), ?EditorView) → bool
// When the selection is empty and at the start of a textblock, select
// the node before that textblock, if possible. This is intended to be
// bound to keys like backspace, after
// [`joinBackward`](#commands.joinBackward) or other deleting
// commands, as a fall-back behavior when the schema doesn't allow
// deletion at the selected point.
function selectNodeBackward(state, dispatch, view) {
  var ref = state.selection;
  var $head = ref.$head;
  var empty = ref.empty;
  var $cut = $head;
  if (!empty) {
    return false;
  }

  if ($head.parent.isTextblock) {
    if (
      view ? !view.endOfTextblock("backward", state) : $head.parentOffset > 0
    ) {
      return false;
    }
    $cut = findCutBefore($head);
  }
  var node = $cut && $cut.nodeBefore;
  if (!node || !NodeSelection.isSelectable(node)) {
    return false;
  }
  if (dispatch) {
    dispatch(
      state.tr
        .setSelection(NodeSelection.create(state.doc, $cut.pos - node.nodeSize))
        .scrollIntoView()
    );
  }
  return true;
}

function findCutBefore($pos) {
  if (!$pos.parent.type.spec.isolating) {
    for (var i = $pos.depth - 1; i >= 0; i--) {
      if ($pos.index(i) > 0) {
        return $pos.doc.resolve($pos.before(i + 1));
      }
      if ($pos.node(i).type.spec.isolating) {
        break;
      }
    }
  }
  return null;
}

// :: (EditorState, ?(tr: Transaction), ?EditorView) → bool
// If the selection is empty and the cursor is at the end of a
// textblock, try to reduce or remove the boundary between that block
// and the one after it, either by joining them or by moving the other
// block closer to this one in the tree structure. Will use the view
// for accurate start-of-textblock detection if given.
function joinForward(state, dispatch, view) {
  var ref = state.selection;
  var $cursor = ref.$cursor;
  if (
    !$cursor ||
    (view
      ? !view.endOfTextblock("forward", state)
      : $cursor.parentOffset < $cursor.parent.content.size)
  ) {
    return false;
  }

  var $cut = findCutAfter($cursor);

  // If there is no node after this, there's nothing to do
  if (!$cut) {
    return false;
  }

  var after = $cut.nodeAfter;
  // Try the joining algorithm
  if (deleteBarrier(state, $cut, dispatch)) {
    return true;
  }

  // If the node above has no content and the node below is
  // selectable, delete the node above and select the one below.
  if (
    $cursor.parent.content.size == 0 &&
    (textblockAt(after, "start") || NodeSelection.isSelectable(after))
  ) {
    if (dispatch) {
      var tr = state.tr.deleteRange($cursor.before(), $cursor.after());
      tr.setSelection(
        textblockAt(after, "start")
          ? Selection.findFrom(tr.doc.resolve(tr.mapping.map($cut.pos)), 1)
          : NodeSelection.create(tr.doc, tr.mapping.map($cut.pos))
      );
      dispatch(tr.scrollIntoView());
    }
    return true;
  }

  // If the next node is an atom, delete it
  if (after.isAtom && $cut.depth == $cursor.depth - 1) {
    if (dispatch) {
      dispatch(
        state.tr.delete($cut.pos, $cut.pos + after.nodeSize).scrollIntoView()
      );
    }
    return true;
  }

  return false;
}

// :: (EditorState, ?(tr: Transaction), ?EditorView) → bool
// When the selection is empty and at the end of a textblock, select
// the node coming after that textblock, if possible. This is intended
// to be bound to keys like delete, after
// [`joinForward`](#commands.joinForward) and similar deleting
// commands, to provide a fall-back behavior when the schema doesn't
// allow deletion at the selected point.
function selectNodeForward(state, dispatch, view) {
  var ref = state.selection;
  var $head = ref.$head;
  var empty = ref.empty;
  var $cut = $head;
  if (!empty) {
    return false;
  }
  if ($head.parent.isTextblock) {
    if (
      view
        ? !view.endOfTextblock("forward", state)
        : $head.parentOffset < $head.parent.content.size
    ) {
      return false;
    }
    $cut = findCutAfter($head);
  }
  var node = $cut && $cut.nodeAfter;
  if (!node || !NodeSelection.isSelectable(node)) {
    return false;
  }
  if (dispatch) {
    dispatch(
      state.tr
        .setSelection(NodeSelection.create(state.doc, $cut.pos))
        .scrollIntoView()
    );
  }
  return true;
}

function findCutAfter($pos) {
  if (!$pos.parent.type.spec.isolating) {
    for (var i = $pos.depth - 1; i >= 0; i--) {
      var parent = $pos.node(i);
      if ($pos.index(i) + 1 < parent.childCount) {
        return $pos.doc.resolve($pos.after(i + 1));
      }
      if (parent.type.spec.isolating) {
        break;
      }
    }
  }
  return null;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Join the selected block or, if there is a text selection, the
// closest ancestor block of the selection that can be joined, with
// the sibling above it.
function joinUp(state, dispatch) {
  var sel = state.selection,
    nodeSel = sel instanceof NodeSelection,
    point;
  if (nodeSel) {
    if (sel.node.isTextblock || !canJoin(state.doc, sel.from)) {
      return false;
    }
    point = sel.from;
  } else {
    point = joinPoint(state.doc, sel.from, -1);
    if (point == null) {
      return false;
    }
  }
  if (dispatch) {
    var tr = state.tr.join(point);
    if (nodeSel) {
      tr.setSelection(
        NodeSelection.create(
          tr.doc,
          point - state.doc.resolve(point).nodeBefore.nodeSize
        )
      );
    }
    dispatch(tr.scrollIntoView());
  }
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Join the selected block, or the closest ancestor of the selection
// that can be joined, with the sibling after it.
function joinDown(state, dispatch) {
  var sel = state.selection,
    point;
  if (sel instanceof NodeSelection) {
    if (sel.node.isTextblock || !canJoin(state.doc, sel.to)) {
      return false;
    }
    point = sel.to;
  } else {
    point = joinPoint(state.doc, sel.to, 1);
    if (point == null) {
      return false;
    }
  }
  if (dispatch) {
    dispatch(state.tr.join(point).scrollIntoView());
  }
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Lift the selected block, or the closest ancestor block of the
// selection that can be lifted, out of its parent node.
function lift(state, dispatch) {
  // console.log('Commands.Lift')

  var ref = state.selection;
  var $from = ref.$from;
  var $to = ref.$to;
  var range = $from.blockRange($to),
    target = range && liftTarget(range);
  if (target == null) {
    return false;
  }
  if (dispatch) {
    dispatch(state.tr.lift(range, target).scrollIntoView());
  }
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// If the selection is in a node whose type has a truthy
// [`code`](#model.NodeSpec.code) property in its spec, replace the
// selection with a newline character.
function newlineInCode(state, dispatch) {
  console.log("newlineInCode");
  var ref = state.selection;
  var $head = ref.$head;
  var $anchor = ref.$anchor;
  if (!$head.parent.type.spec.code || !$head.sameParent($anchor)) {
    return false;
  }
  if (dispatch) {
    dispatch(state.tr.insertText("\n").scrollIntoView());
  }
  console.log("newlineInCode.done");
  return true;
}

function defaultBlockAt(match) {
  for (var i = 0; i < match.edgeCount; i++) {
    var ref = match.edge(i);
    var type = ref.type;
    if (type.isTextblock && !type.hasRequiredAttrs()) {
      return type;
    }
  }
  return null;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// When the selection is in a node with a truthy
// [`code`](#model.NodeSpec.code) property in its spec, create a
// default block after the code block, and move the cursor there.
function exitCode(state, dispatch) {
  var ref = state.selection;
  var $head = ref.$head;
  var $anchor = ref.$anchor;
  if (!$head.parent.type.spec.code || !$head.sameParent($anchor)) {
    return false;
  }
  var above = $head.node(-1),
    after = $head.indexAfter(-1),
    type = defaultBlockAt(above.contentMatchAt(after));
  if (!above.canReplaceWith(after, after, type)) {
    return false;
  }
  if (dispatch) {
    var pos = $head.after(),
      tr = state.tr.replaceWith(pos, pos, type.createAndFill());
    tr.setSelection(Selection.near(tr.doc.resolve(pos), 1));
    dispatch(tr.scrollIntoView());
  }
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// If a block node is selected, create an empty paragraph before (if
// it is its parent's first child) or after it.
function createParagraphNear(state, dispatch) {
  console.log("createParagraphNear");
  var ref = state.selection;
  var $from = ref.$from;
  var $to = ref.$to;
  if ($from.parent.inlineContent || $to.parent.inlineContent) {
    return false;
  }
  var type = defaultBlockAt($from.parent.contentMatchAt($to.indexAfter()));
  if (!type || !type.isTextblock) {
    return false;
  }
  if (dispatch) {
    var side = (!$from.parentOffset && $to.index() < $to.parent.childCount
      ? $from
      : $to
    ).pos;
    var tr = state.tr.insert(side, type.createAndFill());
    tr.setSelection(TextSelection.create(tr.doc, side + 1));
    dispatch(tr.scrollIntoView());
  }
  console.log("createParagraphNear.done");
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// If the cursor is in an empty textblock that can be lifted, lift the
// block.
function liftEmptyBlock(state, dispatch) {
  console.log("Commands.liftEmptyBlock");

  var ref = state.selection;
  var $cursor = ref.$cursor;

  if (!$cursor || $cursor.parent.content.size) {
    return false;
  }

  if ($cursor.depth > 1 && $cursor.after() != $cursor.end(-1)) {
    // console.log('Commands.liftEmptyBlock.Split')

    var before = $cursor.before();

    if (canSplit(state.doc, before)) {
      if (dispatch) {
        dispatch(state.tr.split(before).scrollIntoView());
      }
      console.log("Commands.liftEmptyBlock.split");
      return true;
    }
  }

  // console.log('Commands.liftEmptyBlock.Default')
  var range = $cursor.blockRange(),
    target = range && liftTarget(range);

  if (target == null) {
    return false;
  }

  // console.log('Commands.liftEmptyBlock.target', target, range)

  if (dispatch) {
    dispatch(state.tr.lift(range, target).scrollIntoView());
  }
  console.log("Commands.liftEmptyBlock.done");
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Split the parent block of the selection. If the selection is a text
// selection, also delete its content.
function splitBlock(state, dispatch) {
  console.log("splitBlock");
  var ref = state.selection;
  var $from = ref.$from;
  var $to = ref.$to;

  if (
    state.selection instanceof NodeSelection &&
    state.selection.node.isBlock
  ) {
    if (!$from.parentOffset || !canSplit(state.doc, $from.pos)) {
      return false;
    }
    if (dispatch) {
      dispatch(state.tr.split($from.pos).scrollIntoView());
    }
    console.log("splitBlock.simple");
    return true;
  }

  if (!$from.parent.isBlock) {
    return false;
  }

  if (dispatch) {
    // console.log('splitBlock.dispatch')

    var atEnd = $to.parentOffset == $to.parent.content.size;
    var tr = state.tr;

    if (state.selection instanceof TextSelection) {
      tr.deleteSelection();
    }

    var deflt =
      $from.depth == 0
        ? null
        : defaultBlockAt($from.node(-1).contentMatchAt($from.indexAfter(-1)));
    var types = atEnd && deflt ? [{ type: deflt }] : null;
    var can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);

    if (
      !types &&
      !can &&
      canSplit(tr.doc, tr.mapping.map($from.pos), 1, deflt && [{ type: deflt }])
    ) {
      types = [{ type: deflt }];
      can = true;
    }

    if (can) {
      /*
			console.log('splitBlock.Run2.Types', types)
			console.log('splitBlock.Run2.Default', deflt)
			console.log('splitBlock.Run2.From', $from.pos)
			console.log('splitBlock.Run2.Tr', tr)
			console.log('splitBlock.Run2.Tr.Mapping', tr.mapping)
			console.log('splitBlock.Run2.Mapping', tr.mapping.map($from.pos))
			*/

      // tr.doc.resolve(pos)

      // Let's get the real position of $from after transactions applied
      const reallocPos = tr.mapping.map($from.pos);

      // Let's get the type of an element for the $from
      // console.log('splitBlock.can', $from?.parent)
      let fromType = $from?.parent?.type;

      // Got type?
      if (fromType) {
        // console.log('splitBlock.fromType', fromType)

        // Switch to paragraph
        if (fromType === fromType?.schema?.nodes?.heading) {
          fromType = fromType.schema.nodes.paragraph;
        }

        // Set the working types
        types = [{ type: fromType }];

        /*
				// The type is heading?
				if (fromType === fromType?.schema?.nodes?.heading) {
					console.log('splitBlock.Heading')

					// Switch to paragraph
					fromType = fromType.schema.nodes.paragraph

					// Set level (h1, h2, h3) from the parent
					if ($from?.parent?.attrs?.level) {
						// console.log('splitBlock.Run2.Level', $from.parent.attrs.level)
						// fromType.attrs.level = $from.parent.attrs.level

						// Set new attributes
						types[0].attrs = { level: $from.parent.attrs.level }
					}
				}
				*/
      }

      /**
       * Split the node at the given position
       * https://prosemirror.net/docs/ref/#transform.Transform.split
       */
      tr.split(
        // Given position
        reallocPos, // tr.mapping.map($from.pos),
        // Depth, if depth is greater than one, any number of nodes above that
        1,
        // By default, the parts split off will inherit the node type of the original node. This can be changed by passing an array of types and attributes to use after the split.
        types
        /* (
					// Do I know the type of block under $from?
					fromType ?
						// Use it
						[{ type: fromType }]
						// Use default
						: types // [{ type: deflt }]
				) */
        // types // (!types ? [{ type:deflt }] : types)
      );

      if (
        !atEnd &&
        !$from.parentOffset &&
        // && $from.parent.type != deflt
        $from
          .node(-1)
          .canReplace(
            $from.index(-1),
            $from.indexAfter(-1),
            Fragment.from(deflt.create(), $from.parent)
          )
      ) {
        // console.log('splitBlock.Run3', $from.before())
        tr.setNodeMarkup(tr.mapping.map($from.before()), deflt);
      }
    }

    dispatch(tr.scrollIntoView());
  }

  console.log("splitBlock.done");
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Acts like [`splitBlock`](#commands.splitBlock), but without
// resetting the set of active marks at the cursor.
function splitBlockKeepMarks(state, dispatch) {
  return splitBlock(
    state,
    dispatch &&
      function (tr) {
        var marks =
          state.storedMarks ||
          (state.selection.$to.parentOffset && state.selection.$from.marks());
        if (marks) {
          tr.ensureMarks(marks);
        }
        dispatch(tr);
      }
  );
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Move the selection to the node wrapping the current selection, if
// any. (Will not select the document node.)
function selectParentNode(state, dispatch) {
  var ref = state.selection;
  var $from = ref.$from;
  var to = ref.to;
  var pos;
  var same = $from.sharedDepth(to);
  if (same == 0) {
    return false;
  }
  pos = $from.before(same);
  if (dispatch) {
    dispatch(state.tr.setSelection(NodeSelection.create(state.doc, pos)));
  }
  return true;
}

// :: (EditorState, ?(tr: Transaction)) → bool
// Select the whole document.
function selectAll(state, dispatch) {
  if (dispatch) {
    dispatch(state.tr.setSelection(new AllSelection(state.doc)));
  }
  return true;
}

function joinMaybeClear(state, $pos, dispatch) {
  const _debug = true;

  if (_debug) console.log("joinMaybeClear");

  var before = $pos.nodeBefore,
    after = $pos.nodeAfter,
    index = $pos.index();

  if (!before || !after || !before.type.compatibleContent(after.type)) {
    if (_debug) console.log("joinMaybeClear.incompatible");
    return false;
  }

  if (!before.content.size && $pos.parent.canReplace(index - 1, index)) {
    if (_debug) console.log("joinMaybeClear.empty");
    if (dispatch) {
      dispatch(
        state.tr.delete($pos.pos - before.nodeSize, $pos.pos).scrollIntoView()
      );
    }
    return true;
  }

  if (
    !$pos.parent.canReplace(index, index + 1) ||
    !(after.isTextblock || canJoin(state.doc, $pos.pos))
  ) {
    if (_debug) console.log("joinMaybeClear.cantReplace");
    return false;
  }

  if (dispatch) {
    if (_debug) console.log("joinMaybeClear.dispatch");

    let tr = state.tr;

    tr = tr.clearIncompatible(
      $pos.pos,
      before.type,
      before.contentMatchAt(before.childCount)
    );
    // state = state.apply(tr)

    tr = tr.join($pos.pos);
    // state = state.apply(tr)

    const selfNode = tr.doc.resolve($pos.pos);
    if (_debug) console.log("joinMaybeClear.self", selfNode);

    /* // Check parent
		if (selfNode.depth > 1) { 
			const selfNodeParent = selfNode.node(selfNode.depth - 1)
			if (_debug) console.log('joinMaybeClear.selfParent', selfNodeParent)

			if (selfNodeParent?.type === state.doc.type.schema.nodes.block_element) {
				// Last item? Non-Card?
				if (selfNodeParent?.childCount === 1 && selfNodeParent=== false) {
					if (_debug) console.log('joinMaybeClear.blockElement.lastItem')

					// Remove the block_element
					tr.replace(
						selfNode.before(selfNode.depth - 1),
						selfNode.after(selfNode.depth - 1),
						selfNode.parent.slice(0)
					)
				}
			}
		} */

    // Block_element with last child? Replace with contents
    tr = blockElementLastChild(tr, selfNode);

    let selfDepth = selfNode?.depth > 0 ? selfNode?.depth : 0;
    while (selfDepth > 0) {
      // Get a parent
      const parent = selfNode.node(selfDepth - 1);
      // console.log('joinMaybeClear.open.parent.' + (selfDepth - 1), parent)

      // Inside closed block?
      if (
        // My parent is ..
        (parent?.type?.name === "list_item" ||
          parent?.type?.name === "todo_item" ||
          parent?.type?.name === "block_element") &&
        // .. and it's closed
        parent?.attrs?.open !== true &&
        // and the paragraph is not the first child
        parent?.firstChild !== selfNode?.parent
      ) {
        // Let's open the parent!
        const newAttrs = { ...parent.attrs, ...{ open: true } };

        // console.log('joinMaybeClear.open.parent.' + (selfDepth - 1) + '.closed', newAttrs, parent, selfNode.start(selfDepth - 1))
        // console.log('joinMaybeClear.open.parent.' + (selfDepth - 1) + '.resolved', tr.doc.resolve(selfNode.start(selfDepth - 1)))

        tr.setNodeMarkup(
          selfNode.start(selfDepth - 1) - 1, // get into the last item
          null,
          newAttrs
        );

        // state = state.apply(tr)

        // console.log('joinMaybeClear.open.parent.' + (selfDepth - 1) + '.resolved2', tr.doc.resolve(selfNode.start(selfDepth - 1)))
      }
      selfDepth--;
    }

    /*
		// Are we inside the closed parent?
		if (before?.attrs?.open === false) {
			console.log('joinMaybeClear.open', before)

			newAttrs = { ...before.attrs, ...{ open: true } }

			tr.setNodeMarkup(
				pos.end() - 1, // get into the last item
				null,
				newAttrs
			)
		}
		*/

    dispatch(tr.scrollIntoView());
  }
  return true;
}

/**
 * Block_element Node Delete key reaction
 *
 * @param {object} state
 * @param {object} dispatch
 */
export function blockElementDelete(state, dispatch) {
  console.log("blockElementDelete", state);

  // There is a selection?
  if (!state.selection?.empty) {
    // Do nothing
    console.log("blockElementDelete.empty");
    return false;
  }

  const tr = state.tr;
  const cursor = state.tr.selection?.$cursor;
  const schemaNodes = state.doc.type.schema.nodes;

  // No cursor?
  if (!cursor) {
    // Do nothing
    console.log("blockElementDelete.noCursor");
    return false;
  }

  /* // Are we in a block_element?
	if (cursor.depth >= 2) {
		const parentNode = cursor.node(cursor.depth - 1)
		if (
			parentNode
			&& parentNode?.type === schemaNodes.block_element
		) {
			const parentNodePos = cursor.after(cursor.depth - 1)

			console.log('blockElementDelete.childInBlockElement', parentNodePos, parentNode, tr.doc)

			// Are we not the last node?
			if ((parentNodePos + 1) !== (tr.doc.nodeSize - 1)) {
				// Let's get next Node
				const nextNode = tr.doc.resolve(parentNodePos + 1)

				// It's also a block_element?
				if (
					nextNode
					&& nextNode?.parent?.type === schemaNodes.block_element
				) {
					// Let's skip processing for now
					console.log('blockElementDelete.nextBlockElement.SkipForNow')
					return false
				}
			}
		}
	} */

  // Invalid depth?
  if (cursor?.depth < 3) {
    // Do nothing
    console.log("blockElementDelete.depth");
    return false;
  }

  // Are we inside block_title?
  const blockTitle = cursor.node(cursor.depth - 1);
  if (
    !blockTitle ||
    !blockTitle?.type ||
    blockTitle?.type != schemaNodes.block_title
  ) {
    // Do nothing
    console.log("blockElementDelete.notBlockTitle");
    return false;
  }

  console.log("blockElementDelete.blockTitle", blockTitle);

  // Are we inside block_element?
  const blockElement = cursor.node(cursor.depth - 2);
  if (
    !blockElement ||
    !blockElement?.type ||
    blockElement.type !== schemaNodes.block_element
  ) {
    // Do nothing
    // console.log('blockElementDelete.notBlockElement')
    return false;
  }
  const blockElementStart = cursor.start(cursor.depth - 2);
  const blockElementEnd = cursor.end(cursor.depth - 2);
  const blockElementBefore = cursor.before(cursor.depth - 2);
  const blockElementAfter = cursor.after(cursor.depth - 2);

  console.log("blockElementDelete.blockElement", blockElementEnd, blockElement);

  // Are we at the end of the block_title?
  const blockTitleEnd = cursor.end(cursor.depth - 1);
  const blockTitleParagraphEnd = blockTitleEnd - 1;
  console.log(
    "blockElementDelete.blockTitleEnd",
    blockTitleParagraphEnd,
    cursor.pos
  );

  if (blockTitleParagraphEnd === cursor.pos) {
    console.log("blockElementDelete.End");

    const blockTitleAfter = cursor.after(cursor.depth - 1);

    // Whos next after us?
    const resolvedAfter = tr.doc.resolve(blockTitleAfter + 1);
    let resolvedAfterBefore = resolvedAfter.before();
    let resolvedAfterAfter = resolvedAfter.after();
    console.log("blockElementDelete.resolvedAfter", resolvedAfter);

    // Is this node inside of block_element?
    // console.log('blockElementDelete.resolvedAfter.Within', resolvedAfter.start(), resolvedAfter.end(), resolvedAfter.after(), blockElementEnd)
    if (resolvedAfter.end() > blockElementEnd) {
      // Do nothing
      // console.log('blockElementDelete.notBlockElement')
      return false;
    }

    // Missing type?
    if (!resolvedAfter?.parent?.type) {
      // Do nothing
      // console.log('blockElementDelete.missingType')
      return false;
    }

    // UNPROCESSED OPTION - block_element found as a child
    if (resolvedAfter.parent.type === schemaNodes.block_element) {
      console.log("blockElementDelete.unProcessed.BlockElement", resolvedAfter);
      return;
    }

    // Processing
    if (dispatch) {
      let nodeAfter = resolvedAfter.parent;

      // Are we not a paragraph?
      while (
        nodeAfter.type !== schemaNodes.paragraph &&
        nodeAfter.type !== schemaNodes.heading
      ) {
        // We have no childs?
        if (!nodeAfter?.firstChild) break;

        // Take this child and work with it
        nodeAfter = nodeAfter.firstChild;

        // Change the poses if it's not the one we need
        if (
          nodeAfter.type !== schemaNodes.paragraph &&
          nodeAfter.type !== schemaNodes.heading
        ) {
          resolvedAfterBefore += 1;
          resolvedAfterAfter = resolvedAfterBefore + nodeAfter.nodeSize;
        }
      }

      // Still not found? Can't process
      if (
        (nodeAfter.type !== schemaNodes.paragraph &&
          nodeAfter.type !== schemaNodes.heading) ||
        !nodeAfter?.firstChild ||
        !nodeAfter?.content
      ) {
        // Do nothing
        console.log("blockElementDelete.cantFindParagraph");
        return false;
      }

      console.log(
        "blockElementDelete.Paragraph",
        nodeAfter,
        nodeAfter.content.cut(0)
      );

      // если != 1 - то там точно что-то еще есть
      // 1 для двух параграфов
      // resolvedAfter.parent.childCount == 2

      // Was that the last child in block_element?
      if (
        resolvedAfter.after() === blockElementEnd &&
        (!resolvedAfter.parent?.childCount ||
          resolvedAfter.parent.childCount === 1)
      ) {
        console.log("blockElementDelete.Last");

        // Get the block_element positions
        // const blockElementNewEnd = tr.mapping.map(blockElementEnd)
        // console.log('blockElementDelete.blockElementPos', blockElementStart, blockElementEnd, blockElementNewEnd)

        // console.log('blockElementDelete.blockElementNewTitle', cursor.node(cursor.depth - 1))

        // Do we know where the text is?
        if (!blockTitle?.firstChild?.firstChild) {
          // Do nothing
          console.log("blockElementDelete.noText");
          return true;
        }

        // const titleNodeSlice =
        console.log("blockElementDelete.Slice", blockTitle.slice(0));

        // Replace the block_element with a paragraph
        tr.replace(blockElementBefore, blockElementAfter, blockTitle.slice(0));

        // Copy the contents after the enter
        tr.insert(
          blockElementStart + blockTitle.firstChild.firstChild.nodeSize,
          nodeAfter.content.cut(0)
        );

        // Move the cursor
        const cursorNewPos =
          blockElementStart + blockTitle.firstChild.firstChild.nodeSize; //  + nodeAfter.content.size
        tr.setSelection(TextSelection.create(tr.doc, cursorNewPos));

        // There are children still after this
      } else {
        console.log("blockElementDelete.Other");

        // Remove the paragraph we cut
        tr.delete(resolvedAfterBefore, resolvedAfterAfter);

        // Copy the contents after the enter
        tr.insert(cursor.pos, nodeAfter.content.cut(0));

        // Move the cursor
        tr.setSelection(TextSelection.create(tr.doc, cursor.pos));
      }

      // Run it!
      dispatch(tr.scrollIntoView());

      // Success
      return true;
    }

    // Let's look for the child within the block_element (except for the block_title)
    // for (j = 0;j < .)
  }

  // console.log()

  // Are we inside the block_element?
}

/**
 * Empty Nodes removal
 *
 * @param {object} state
 * @param {object} dispatch
 */
function deleteEmptySelf(state, dispatch) {
  console.log("deleteEmptySelf", state);

  // There is a selection?
  if (!state.selection?.empty) {
    // Do nothing
    // console.log('deleteEmptySelf.empty')
    return false;
  }

  let tr = state.tr;
  const from = state.tr.selection.$from;
  const to = state.tr.selection.$to;
  const schemaNodes = state.doc.type.schema.nodes;

  // From and to diff?
  if (from.pos !== to.pos) {
    // Do nothing
    // console.log('deleteEmptySelf.pos')
    return false;
  }

  /*
	// No valid parent?
	if (!from?.parent?.textContent) {
		// Do nothing
		console.log('deleteEmptySelf.textContent')
		return false
	}
	*/

  // There is some content inside element?
  if (from?.parent?.textContent !== "") {
    // Do nothing
    console.log("deleteEmptySelf.textContent.Nonempty");
    return false;
  }

  // Am I in list?
  if (from?.depth > 2) {
    // Get parent
    const parentNode = from.node(from.depth - 1);

    // List?
    if (
      parentNode &&
      (parentNode?.type === schemaNodes.todo_item ||
        parentNode?.type === schemaNodes.list_item)
    ) {
      const parentBefore = from.before(from.depth - 1);
      const parentAfter = from.after(from.depth - 1);

      // Do we have pos?
      if (parentBefore && parentAfter) {
        console.log("deleteEmptySelf.InList");

        // Dispatch it!
        if (dispatch) {
          // Remove parent
          tr.delete(parentBefore, parentAfter);

          // Dispatch it and focus!
          dispatch(tr.scrollIntoView());
        }

        return true;
      }
    }
  }

  // Dispatch it!
  if (dispatch) {
    // Remove self
    tr.delete(from.before(), from.after());

    // Block_element with last child? Replace with contents
    tr = blockElementLastChild(tr, from);

    // Dispatch it and focus!
    dispatch(tr.scrollIntoView());
  }

  // Do not allow other functions to run
  console.log("deleteEmptySelf.Worked");
  return true;
}

/**
 * Empty Nodes removal
 *
 * @param {object} state
 * @param {object} dispatch
 */
export function backspaceDeleteEmpty(state, dispatch) {
  console.log("backspaceDeleteEmpty", state);

  // There is a selection?
  if (!state.selection?.empty) {
    // Do nothing
    console.log("backspaceDeleteEmpty.empty");
    return false;
  }

  let tr = state.tr;
  const from = state.tr.selection.$from;
  const to = state.tr.selection.$to;
  const schemaNodes = state.doc.type.schema.nodes;

  // From and to diff?
  if (from.pos !== to.pos) {
    // Do nothing
    console.log("backspaceDeleteEmpty.pos");
    return false;
  }

  /*
	// No valid parent?
	if (!from?.parent?.textContent) {
		// Do nothing
		console.log('backspaceDeleteEmpty.textContent')
		return false
	}
	*/

  console.log("backspaceDeleteEmpty.continue");

  // There is some content inside element?
  if (from?.parent?.textContent !== "") {
    // Do nothing
    console.log("backspaceDeleteEmpty.textContent.Nonempty");
    return false;
  }

  // Am I in list?
  if (from?.depth > 2) {
    console.log("backspaceDeleteEmpty.depth2");

    // Get parent
    const parentNode = from.node(from.depth - 1);

    // List?
    if (
      parentNode &&
      (parentNode?.type === schemaNodes.todo_item ||
        parentNode?.type === schemaNodes.list_item)
    ) {
      const parentBefore = from.before(from.depth - 1);
      const parentAfter = from.after(from.depth - 1);

      console.log("backspaceDeleteEmpty.list2");

      // Do we have pos?
      if (parentBefore && parentAfter) {
        console.log("deleteEmptySelf.InList");

        // Dispatch it!
        if (dispatch) {
          // Remove parent
          tr.delete(parentBefore, parentAfter);

          // Dispatch it and focus!
          dispatch(tr.scrollIntoView());
        }

        return true;
      }
    }
  }

  console.log("backspaceDeleteEmpty.proceed");

  // Dispatch it!
  if (dispatch) {
    // Remove self

    tr.delete(from.before(), from.after());

    // Block_element with last child? Replace with contents
    tr = blockElementLastChild(tr, from);
    console.log("backspaceDeleteEmpty.tr", tr);

    // Move cursor
    state = state.apply(tr);
    cursorParagraphBefore(
      state,
      tr,
      tr?.selection?.$cursor?.pos < from.before()
        ? tr.selection.$cursor.pos
        : from.before()
    );

    /*
		const endPos = findParagraphBefore(tr.doc, from.before())
		console.log('findParagraphBefore', endPos)
		tr.setSelection(TextSelection.create(tr.doc, endPos))
		*/

    // Dispatch it and focus!
    dispatch(tr.scrollIntoView());
  }

  // Do not allow other functions to run
  console.log("backspaceDeleteEmpty.Worked");
  return true;
}

function deleteBarrier(state, $cut, dispatch) {
  console.log("deleteBarrier"); // , $cut, before, after
  var before = $cut.nodeBefore,
    after = $cut.nodeAfter,
    conn,
    match;
  if (before.type.spec.isolating || after.type.spec.isolating) {
    return false;
  }
  if (joinMaybeClear(state, $cut, dispatch)) {
    return true;
  }

  if (
    $cut.parent.canReplace($cut.index(), $cut.index() + 1) &&
    (conn = (match = before.contentMatchAt(before.childCount)).findWrapping(
      after.type
    )) &&
    match.matchType(conn[0] || after.type).validEnd
  ) {
    // console.log('deleteBarrier.cut', $cut, before, after)
    console.log("deleteBarrier.JoinUp");

    if (dispatch) {
      var end = $cut.pos + after.nodeSize,
        wrap = Fragment.empty;
      for (var i = conn.length - 1; i >= 0; i--) {
        wrap = Fragment.from(conn[i].create(null, wrap));
      }

      wrap = Fragment.from(before.copy(wrap));
      var tr = state.tr.step(
        new ReplaceAroundStep(
          $cut.pos - 1,
          end,
          $cut.pos,
          end,
          new Slice(wrap, 1, 0),
          conn.length,
          true
        )
      );

      // Are we joining it into a block_element?
      if (before.type === before.type.schema.nodes.block_element) {
        console.log("deleteBarrier.blockElement.noJoin");
      } else {
        /*
				console.log('deleteBarrier.JoinDown')

				var joinAt = end + 2 * conn.length;
				// var $pos = tr.doc.resolve(joinAt)
				// console.log('deleteBarrier.canJoin', joinAt, tr.doc, $pos)
				if (canJoin(tr.doc, joinAt)) {
					// console.log('deleteBarrier.joinAt')
					tr.join(joinAt);
				}
				*/
      }

      dispatch(tr.scrollIntoView());
    }

    return true;
  }

  /*
		Next element is block-element?
	*/
  if (after.type === after.type.schema.nodes.block_element) {
    const blockElement = after;
    console.log("deleteBarrier.blockElement", $cut, blockElement);

    /*
			OLD block_title
		*/

    // Resolve the block_title
    const blockTitle = state.tr.doc.resolve($cut.pos + 2);

    // Do we have the title?
    if (
      blockTitle &&
      blockTitle?.parent?.type === blockElement.type.schema.nodes.block_title &&
      blockTitle?.parent?.firstChild?.content
    ) {
      console.log("deleteBarrier.blockElement.blockTitle", blockTitle);

      // Get the block_title contents as a Fragment
      const blockTitleContents = blockTitle.parent.firstChild.content.cut(
        0,
        blockTitle.parent.firstChild.content.size
      );

      // Processing?
      if (dispatch) {
        let tr = state.tr;

        // For each child in block_element, insert after the Node with the cursor
        for (let j = 0; j < blockElement.childCount; j++) {
          const currentChild = blockElement.child(j);
          if (currentChild?.type === blockElement.type.schema.nodes.block_title)
            continue;
          console.log("deleteBarrier.blockElement.Child", currentChild);

          // Copy the contents after the enter
          tr.insert($cut.pos, currentChild.cut(0, null));
        }

        // Got contents?
        if (blockTitleContents) {
          console.log(
            "deleteBarrier.blockElement.blockTitleContents",
            blockTitleContents
          );

          // Copy the contents inside
          tr.insert($cut.pos - 1, blockTitleContents);
        }

        // Remove block_title
        // tr.delete(blockTitle.before(), blockTitle.after())

        // Remove block_element
        console.log("deleteBarrier.blockElement.Mapping", tr, tr.mapping);
        const blockElementPos = tr.mapping.map($cut.pos);
        console.log(
          "deleteBarrier.blockElement.blockElementPos",
          $cut.pos,
          blockElementPos
        );
        tr.delete(blockElementPos, blockElementPos + blockElement.nodeSize);

        // Move the cursor
        tr.setSelection(TextSelection.create(tr.doc, $cut.pos - 1));

        // Run it!
        dispatch(tr.scrollIntoView());

        return true;
      }
    }
  }

  var selAfter = Selection.findFrom($cut, 1);
  var range = selAfter && selAfter.$from.blockRange(selAfter.$to),
    target = range && liftTarget(range);

  if (target != null && target >= $cut.depth) {
    console.log("deleteBarrier.depth"); // , target, $cut, selAfter, range

    if (dispatch) {
      dispatch(state.tr.lift(range, target).scrollIntoView());
    }
    return true;
  }

  return false;
}

// Parameterized commands

// :: (NodeType, ?Object) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Wrap the selection in a node of the given type with the given
// attributes.
function wrapIn(nodeType, attrs) {
  return function (state, dispatch) {
    var ref = state.selection;
    var $from = ref.$from;
    var $to = ref.$to;
    var range = $from.blockRange($to),
      wrapping = range && findWrapping(range, nodeType, attrs);
    if (!wrapping) {
      return false;
    }
    if (dispatch) {
      dispatch(state.tr.wrap(range, wrapping).scrollIntoView());
    }
    return true;
  };
}

// :: (NodeType, ?Object) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Returns a command that tries to set the selected textblocks to the
// given node type with the given attributes.
function setBlockType(nodeType, attrs) {
  return function (state, dispatch) {
    var ref = state.selection;
    var from = ref.from;
    var to = ref.to;
    var applicable = false;
    state.doc.nodesBetween(from, to, function (node, pos) {
      if (applicable) {
        return false;
      }
      if (!node.isTextblock || node.hasMarkup(nodeType, attrs)) {
        return;
      }
      if (node.type == nodeType) {
        applicable = true;
      } else {
        var $pos = state.doc.resolve(pos),
          index = $pos.index();
        applicable = $pos.parent.canReplaceWith(index, index + 1, nodeType);
      }
    });
    if (!applicable) {
      return false;
    }
    if (dispatch) {
      dispatch(
        state.tr.setBlockType(from, to, nodeType, attrs).scrollIntoView()
      );
    }
    return true;
  };
}

function markApplies(doc, ranges, type) {
  var loop = function (i) {
    var ref = ranges[i];
    var $from = ref.$from;
    var $to = ref.$to;
    var can = $from.depth == 0 ? doc.type.allowsMarkType(type) : false;
    doc.nodesBetween($from.pos, $to.pos, function (node) {
      if (can) {
        return false;
      }
      can = node.inlineContent && node.type.allowsMarkType(type);
    });
    if (can) {
      return { v: true };
    }
  };

  for (var i = 0; i < ranges.length; i++) {
    var returned = loop(i);

    if (returned) return returned.v;
  }
  return false;
}

// :: (MarkType, ?Object) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Create a command function that toggles the given mark with the
// given attributes. Will return `false` when the current selection
// doesn't support that mark. This will remove the mark if any marks
// of that type exist in the selection, or add it otherwise. If the
// selection is empty, this applies to the [stored
// marks](#state.EditorState.storedMarks) instead of a range of the
// document.
function toggleMark(markType, attrs) {
  return function (state, dispatch) {
    var ref = state.selection;
    var empty = ref.empty;
    var $cursor = ref.$cursor;
    var ranges = ref.ranges;
    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }
    if (dispatch) {
      if ($cursor) {
        if (markType.isInSet(state.storedMarks || $cursor.marks())) {
          dispatch(state.tr.removeStoredMark(markType));
        } else {
          dispatch(state.tr.addStoredMark(markType.create(attrs)));
        }
      } else {
        var has = false,
          tr = state.tr;
        for (var i = 0; !has && i < ranges.length; i++) {
          var ref$1 = ranges[i];
          var $from = ref$1.$from;
          var $to = ref$1.$to;
          has = state.doc.rangeHasMark($from.pos, $to.pos, markType);
        }
        for (var i$1 = 0; i$1 < ranges.length; i$1++) {
          var ref$2 = ranges[i$1];
          var $from$1 = ref$2.$from;
          var $to$1 = ref$2.$to;
          if (has) {
            tr.removeMark($from$1.pos, $to$1.pos, markType);
          } else {
            tr.addMark($from$1.pos, $to$1.pos, markType.create(attrs));
          }
        }
        dispatch(tr.scrollIntoView());
      }
    }
    return true;
  };
}

function wrapDispatchForJoin(dispatch, isJoinable) {
  return function (tr) {
    if (!tr.isGeneric) {
      return dispatch(tr);
    }

    var ranges = [];
    for (var i = 0; i < tr.mapping.maps.length; i++) {
      var map = tr.mapping.maps[i];
      for (var j = 0; j < ranges.length; j++) {
        ranges[j] = map.map(ranges[j]);
      }
      map.forEach(function (_s, _e, from, to) {
        return ranges.push(from, to);
      });
    }

    // Figure out which joinable points exist inside those ranges,
    // by checking all node boundaries in their parent nodes.
    var joinable = [];
    for (var i$1 = 0; i$1 < ranges.length; i$1 += 2) {
      var from = ranges[i$1],
        to = ranges[i$1 + 1];
      var $from = tr.doc.resolve(from),
        depth = $from.sharedDepth(to),
        parent = $from.node(depth);
      for (
        var index = $from.indexAfter(depth), pos = $from.after(depth + 1);
        pos <= to;
        ++index
      ) {
        var after = parent.maybeChild(index);
        if (!after) {
          break;
        }
        if (index && joinable.indexOf(pos) == -1) {
          var before = parent.child(index - 1);
          if (before.type == after.type && isJoinable(before, after)) {
            joinable.push(pos);
          }
        }
        pos += after.nodeSize;
      }
    }
    // Join the joinable points
    joinable.sort(function (a, b) {
      return a - b;
    });
    for (var i$2 = joinable.length - 1; i$2 >= 0; i$2--) {
      if (canJoin(tr.doc, joinable[i$2])) {
        tr.join(joinable[i$2]);
      }
    }
    dispatch(tr);
  };
}

// :: ((state: EditorState, ?(tr: Transaction)) → bool, union<(before: Node, after: Node) → bool, [string]>) → (state: EditorState, ?(tr: Transaction)) → bool
// Wrap a command so that, when it produces a transform that causes
// two joinable nodes to end up next to each other, those are joined.
// Nodes are considered joinable when they are of the same type and
// when the `isJoinable` predicate returns true for them or, if an
// array of strings was passed, if their node type name is in that
// array.
function autoJoin(command, isJoinable) {
  if (Array.isArray(isJoinable)) {
    var types = isJoinable;
    isJoinable = function (node) {
      return types.indexOf(node.type.name) > -1;
    };
  }
  return function (state, dispatch) {
    return command(
      state,
      dispatch && wrapDispatchForJoin(dispatch, isJoinable)
    );
  };
}

// :: (...[(EditorState, ?(tr: Transaction), ?EditorView) → bool]) → (EditorState, ?(tr: Transaction), ?EditorView) → bool
// Combine a number of command functions into a single function (which
// calls them one by one until one returns true).
function chainCommands() {
  var commands = [],
    len = arguments.length;
  while (len--) commands[len] = arguments[len];

  return function (state, dispatch, view) {
    for (var i = 0; i < commands.length; i++) {
      if (commands[i](state, dispatch, view)) {
        return true;
      }
    }
    return false;
  };
}

// :: (MarkType, ?Object) → (state: EditorState, dispatch: ?(tr: Transaction)) → bool
// Create a command function that toggles the given mark with the
// given attributes. Will return `false` when the current selection
// doesn't support that mark. This will remove the mark if any marks
// of that type exist in the selection, or add it otherwise. If the
// selection is empty, this applies to the [stored
// marks](#state.EditorState.storedMarks) instead of a range of the
// document.
export function myToggleMark(markType, attrs) {
  return function (state, dispatch) {
    var ref = state.selection;
    var empty = ref.empty;
    var $cursor = ref.$cursor;
    var ranges = ref.ranges;
    if ((empty && !$cursor) || !markApplies(state.doc, ranges, markType)) {
      return false;
    }
    if (dispatch) {
      if ($cursor) {
        if (markType.isInSet(state.storedMarks || $cursor.marks())) {
          dispatch(state.tr.removeStoredMark(markType));
        } else {
          dispatch(state.tr.addStoredMark(markType.create(attrs)));
        }
      } else {
        var has = false,
          tr = state.tr;
        for (var i = 0; !has && i < ranges.length; i++) {
          var ref$1 = ranges[i];
          var $from = ref$1.$from;
          var $to = ref$1.$to;
          has = state.doc.rangeHasMark($from.pos, $to.pos, markType);
        }
        for (var i$1 = 0; i$1 < ranges.length; i$1++) {
          var ref$2 = ranges[i$1];
          var $from$1 = ref$2.$from;
          var $to$1 = ref$2.$to;
          if (has) {
            tr.removeMark($from$1.pos, $to$1.pos, markType);
          }
          // else {
          tr.addMark($from$1.pos, $to$1.pos, markType.create(attrs));
          // }
        }
        dispatch(tr.scrollIntoView());
      }
    }
    return true;
  };
}

export {
  autoJoin,
  chainCommands,
  createParagraphNear,
  deleteSelection,
  exitCode,
  joinBackward,
  joinDown,
  joinForward,
  joinUp,
  lift,
  liftEmptyBlock,
  newlineInCode,
  selectAll,
  selectNodeBackward,
  selectNodeForward,
  selectParentNode,
  setBlockType,
  splitBlock,
  splitBlockKeepMarks,
  toggleMark,
  wrapIn,
  deleteEmptySelf,
};
//# sourceMappingURL=index.es.js.map
